import {
    ButtonSizes,
    ButtonStyles,
    Icon,
    Icons,
    Label,
    List,
    ListItem,
    MenuButton,
    Search,
    showAlert,
    Tooltip
} from '@silverfort/sf-components';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ENTITY_IDENTIFIER_TYPES, ServiceAccountEntity } from '../../ServiceAccountsTypes';
import ResourcePicker from '../../../../../components/Controls/ResourcePicker/ResourcePicker';
import {
    AdTypeOptions,
    calcResourceIcon
} from '../../../../../components/Controls/ResourcePicker/ResourcePickerConsts';
import { HTTP_OPTIONS, KRBTGT, SEARCH_MIN_CHARS } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../../../../components/utils';
import { cloneDeep } from 'lodash';
import { useImportIpsFromCsv } from '../../hooks/useImportIpsFromCsv';

interface EntitiesPickerTabProps {
    entityType: string;
    entities: ServiceAccountEntity[];
    setUpdatedEntities: (entities: ServiceAccountEntity[]) => void;
    disabled: boolean;
}

const ENTITY_TYPE_TO_ICON = {
    [ENTITY_IDENTIFIER_TYPES.IP]: Icons.IP,
    [ENTITY_IDENTIFIER_TYPES.NAME]: Icons.Server,
    [ENTITY_IDENTIFIER_TYPES.GUID]: Icons.Server
};

const DEFAULT_AD_TYPES = Object.values(AdTypeOptions);

const CSV_IMPORT_TOOLTIP = 'Supports up to 1000 IP addresses per CSV, excluding subnets. Maximum file size is 1 MB.';

const EntitiesPickerTab: FC<EntitiesPickerTabProps> = ({ entityType, entities, setUpdatedEntities, disabled }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [openManualSelection, setOpenManualSelection] = useState(false);
    const [openDetectedSelection, setOpenDetectedSelection] = useState(false);
    const [matchedResources, setMatchedResources] = useState<ServiceAccountEntity[]>([]);
    const [matchedIps, setMatchedIps] = useState<string[]>([]);
    const [matchedDetectedResources, setMatchedDetectedResources] = useState<ServiceAccountEntity[]>([]);
    const [matchedDetectedIps, setMatchedDetectedIps] = useState<string[]>([]);
    const [resourceFilters, setResourceFilters] = useState<{ searchTerm?: string; adTypes?: string[] }>({});
    const [loadingResources, setLoadingResources] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { importIpsFromCsv } = useImportIpsFromCsv();

    const allowedEntities = useMemo(() => [...entities.filter((entity) => entity.is_allowed)], [entities]);
    const detectedEntities = useMemo(() => [...entities.filter((entity) => !!entity.hits)], [entities]);

    const filteredAllowedEntities = useMemo(
        () => [
            ...allowedEntities.filter((entity) =>
                entity.identifier_type === ENTITY_IDENTIFIER_TYPES.GUID
                    ? entity.name?.toLowerCase().includes(searchTerm.toLowerCase())
                    : entity.identifier?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        ],
        [allowedEntities, searchTerm]
    );

    useEffect(() => {
        const matchedIps = detectedEntities
            .filter((item) => item.identifier_type === ENTITY_IDENTIFIER_TYPES.IP)
            .map((item) => item.identifier);
        setMatchedDetectedIps(matchedIps);
        setMatchedIps(matchedIps);
        setMatchedDetectedResources(
            detectedEntities.filter((item) => item.identifier_type !== ENTITY_IDENTIFIER_TYPES.IP)
        );
    }, [detectedEntities]);

    const searchResources = useCallback(async () => {
        try {
            let escapedQuery = resourceFilters.searchTerm?.trim() || '';
            if (escapedQuery === '' || escapedQuery.length < SEARCH_MIN_CHARS) {
                setMatchedResources([]);
            } else {
                escapedQuery = encodeURIComponent(escapedQuery);

                setLoadingResources(true);

                const reqUrl = `/users/searchEntitiesByTypes/${escapedQuery}`;

                const res: { data: { Results: any[] } | null } = await httpGet(reqUrl, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: { hideDeleted: true, entitiesTypes: resourceFilters.adTypes || DEFAULT_AD_TYPES }
                    }
                });

                setMatchedResources(res.data?.Results || []);
            }
        } catch (e) {
            console.error('Failed to search resources for query ' + resourceFilters.searchTerm, e);
        } finally {
            setLoadingResources(false);
        }
    }, [resourceFilters.adTypes, resourceFilters.searchTerm]);

    useEffect(() => {
        searchResources();
    }, [searchResources]);

    const handleResourceSearch = (val, type) => {
        if (type === 'ips') {
            const matchedIps = detectedEntities
                .filter(
                    (item) => item.identifier_type === ENTITY_IDENTIFIER_TYPES.IP && item.identifier.startsWith(val)
                )
                .map((item) => item.identifier);

            setMatchedDetectedIps(matchedIps);
            setMatchedIps(matchedIps);
        } else {
            setMatchedDetectedResources(
                detectedEntities.filter(
                    (item) =>
                        item.identifier_type !== ENTITY_IDENTIFIER_TYPES.IP &&
                        (item.identifier.toLowerCase().includes(val.toLowerCase()) ||
                            (item.name && item.name.toLowerCase().includes(val.toLowerCase())))
                )
            );

            setResourceFilters({ ...resourceFilters, searchTerm: val });
        }
    };

    const closeModals = () => {
        setOpenDetectedSelection(false);
        setOpenManualSelection(false);
        setResourceFilters({});
        resetDetectedMatch();
    };

    const handleImportFromCsv = useCallback(
        async (file: File) => {
            try {
                const validIps = await importIpsFromCsv(file);

                const updatedEntities = cloneDeep(entities);
                const existingAllowedIps = new Set(
                    entities
                        .filter((e) => e.identifier_type === ENTITY_IDENTIFIER_TYPES.IP && e.is_allowed)
                        .map((e) => e.identifier)
                );
                const ipIndexMap = new Map(
                    updatedEntities.flatMap((e, i) =>
                        e.identifier_type === ENTITY_IDENTIFIER_TYPES.IP ? [[e.identifier, i]] : []
                    )
                );
                let addedCount = 0;

                for (const ip of validIps) {
                    if (existingAllowedIps.has(ip)) continue;
                    existingAllowedIps.add(ip);

                    const existingEntityIndex = ipIndexMap.get(ip) ?? -1;

                    if (existingEntityIndex !== -1) {
                        updatedEntities[existingEntityIndex] = { ...updatedEntities[existingEntityIndex], is_allowed: true };
                    } else {
                        updatedEntities.push({
                            identifier: ip,
                            identifier_type: ENTITY_IDENTIFIER_TYPES.IP,
                            is_allowed: true
                        });
                    }
                    addedCount++;
                }

                if (addedCount > 0) {
                    setUpdatedEntities(updatedEntities);
                }

                showAlert(
                    `${addedCount} IP ${
                        addedCount === 1 ? 'address' : 'addresses'
                    } added\nSave the policy to apply these changes`,
                    'success'
                );
            } catch {
                showAlert('CSV import failed. Review the file and try again', 'error');
            }
        },
        [entities, importIpsFromCsv, setUpdatedEntities]
    );

    const handleAddAllDetected = useCallback(() => {
        const updatedEntities: ServiceAccountEntity[] = [];
        detectedEntities.forEach((entity) => {
            if (!entity.is_allowed) {
                updatedEntities.push({ ...entity, is_allowed: true });
            }
        });
        setUpdatedEntities([...updatedEntities, ...allowedEntities]);
        closeModals();
    }, [closeModals, entities, setUpdatedEntities]);

    const handleResourceSelect = useCallback(
        (item, type) => {
            const updatedEntities = cloneDeep(entities);

            const existingEntityIndex = updatedEntities.findIndex((entity) => {
                if (type === 'resources') {
                    const isSameName =
                        entity.identifier_type === ENTITY_IDENTIFIER_TYPES.NAME &&
                        entity.identifier.toLowerCase() === (item.name || item.identifier).toLowerCase();
                    const isSameGuid =
                        entity.identifier_type === ENTITY_IDENTIFIER_TYPES.GUID && entity.identifier === item.guid;
                    return isSameGuid || isSameName;
                } else if (type === 'ips') {
                    return (
                        (entity.identifier_type === ENTITY_IDENTIFIER_TYPES.IP && entity.identifier === item) ||
                        (entity.identifier === item.split('/')[0] && item.split('/')[1] === '32')
                    );
                }
                return false;
            });

            if (existingEntityIndex !== -1) {
                const existingEntity = { ...updatedEntities[existingEntityIndex] };
                updatedEntities.splice(existingEntityIndex, 1);
                existingEntity.is_allowed = true;
                updatedEntities.push(existingEntity);
                setUpdatedEntities(updatedEntities);
            } else {
                if (type === 'resources') {
                    const serviceAccountEntity: ServiceAccountEntity = {
                        ...item,
                        identifier: item.guid,
                        identifier_type: ENTITY_IDENTIFIER_TYPES.GUID,
                        is_allowed: true
                    };
                    updatedEntities.push(serviceAccountEntity);
                } else if (type === 'ips') {
                    const calculatedIP = item.split('/')[1] === '32' ? item.split('/')[0] : item;
                    const serviceAccountEntity: ServiceAccountEntity = {
                        identifier: calculatedIP,
                        identifier_type: ENTITY_IDENTIFIER_TYPES.IP,
                        is_allowed: true
                    };
                    updatedEntities.push(serviceAccountEntity);
                }
            }
            setUpdatedEntities(updatedEntities);
        },
        [entities, setUpdatedEntities]
    );

    const handleResourceRemoval = useCallback(
        (item) => {
            const updatedEntities = entities.map((entity) => {
                if (entity.identifier === item.identifier) {
                    return { ...entity, is_allowed: false };
                }
                return entity;
            });
            setUpdatedEntities(updatedEntities);
        },
        [entities, setUpdatedEntities]
    );

    const resetDetectedMatch = () => {
        setMatchedDetectedIps(
            detectedEntities
                .filter((item) => item.identifier_type === ENTITY_IDENTIFIER_TYPES.IP)
                .map((item) => item.identifier)
        );
        setMatchedDetectedResources(
            detectedEntities.filter((item) => item.identifier_type !== ENTITY_IDENTIFIER_TYPES.IP)
        );
    };

    const calcDetectedIcon = (item) => {
        const iconColorClass = shouldEntityBeDisabled(item) ? 'text-sf-primary-300' : 'text-sf-primary-500';
        if (detectedEntities.find((entity) => entity.identifier === item.identifier)) {
            return () => (
                <div>
                    <Tooltip title="Detected by Silverfort" placement="top">
                        <Icon icon={Icons.CircleCheck} className={iconColorClass} />
                    </Tooltip>
                </div>
            );
        }
    };

    const calcEntityIcon = (item: ServiceAccountEntity) => {
        if (item.type) {
            return calcResourceIcon(item);
        }
        return ENTITY_TYPE_TO_ICON[item.identifier_type];
    };

    const calcEntityTitle = (item: ServiceAccountEntity) => {
        let title = item.name || item.identifier;
        if (item.is_disabled) {
            title += ' (Disabled)';
        }
        return title;
    };

    const calcEntitySubtitle = (item: ServiceAccountEntity) => {
        if (item.domain_name) {
            return `(${item.domain_name})`;
        }
    };

    function shouldEntityBeDisabled(item) {
        return item.name?.toLowerCase() === KRBTGT;
    }

    return (
        <div className="border border-sf-gray-100 rounded-lg w-full h-[288px]">
            <div className="py-2 pr-4 pl-6 border-b h-14 w-full flex items-center justify-between">
                <Label text={`Selected ${entityType} (${allowedEntities.length})`} />
                <div className="flex gap-2 items-center">
                    <Search border onChange={setSearchTerm} />
                    <MenuButton
                        disabled={disabled}
                        openByDefault={false}
                        buttonSize={ButtonSizes.Medium}
                        icon={Icons.Add}
                        iconClassName={`w-fit`}
                        className="right-0"
                        text="Add"
                        holdPressed={openManualSelection || openDetectedSelection}
                        buttonStyle={ButtonStyles.Primary}
                        content={
                            <List className="p-[0px] min-w-[184px]">
                                <ListItem
                                    title="Add manually"
                                    IconLeft={() => <Icon icon={Icons.UserCheck} className="text-sf-gray-400" />}
                                    onClick={() => {
                                        setOpenDetectedSelection(false);
                                        setOpenManualSelection(true);
                                    }}
                                />
                                <ListItem
                                    title="Detected by Silverfort"
                                    IconLeft={() => <Icon icon={Icons.CircleCheck} className="text-sf-gray-400" />}
                                    onClick={() => {
                                        setOpenManualSelection(false);
                                        setOpenDetectedSelection(true);
                                    }}
                                />
                                <Tooltip title={CSV_IMPORT_TOOLTIP}>
                                    <div>
                                        <ListItem
                                            title="Import IPs from CSV"
                                            IconLeft={() => <Icon icon={Icons.Upload} className="text-sf-gray-400" />}
                                            onClick={() => fileInputRef.current?.click()}
                                        />
                                    </div>
                                </Tooltip>
                            </List>
                        }
                    />
                </div>
            </div>
            {(openDetectedSelection || openManualSelection) && (
                <div>
                    <div className="w-full absolute top-[180px] left-[125px]">
                        <ResourcePicker
                            shouldAddSubnet
                            loading={loadingResources}
                            title={openManualSelection ? 'Available resources' : 'Resources detected by Silverfort'}
                            showAddAll={openDetectedSelection}
                            onAddAll={handleAddAllDetected}
                            onSelect={handleResourceSelect}
                            hideNewIPButton={openDetectedSelection}
                            resources={openDetectedSelection ? matchedDetectedResources : matchedResources}
                            ips={openDetectedSelection ? matchedDetectedIps : matchedIps}
                            selectedResources={allowedEntities.filter(
                                (item) => item.identifier_type !== ENTITY_IDENTIFIER_TYPES.IP
                            )}
                            selectedIPs={allowedEntities
                                .filter((item) => item.identifier_type === ENTITY_IDENTIFIER_TYPES.IP)
                                .map((item) => item.identifier)}
                            onSearchChange={handleResourceSearch}
                            showAdTypeFilter={openManualSelection}
                            onAdTypeChange={(types) => setResourceFilters({ ...resourceFilters, adTypes: types })}
                            onAction={() => closeModals()}
                            onClose={() => closeModals()}
                            shouldShowTypeDisplay={true}
                        />
                    </div>
                </div>
            )}
            {!!filteredAllowedEntities?.length ? (
                <div className="h-[230px] w-full overflow-auto">
                    <List>
                        {filteredAllowedEntities.map((item, i) => {
                            return (
                                <ListItem
                                    key={i}
                                    className="cursor-default"
                                    title={calcEntityTitle(item)}
                                    subtitle={calcEntitySubtitle(item)}
                                    IconLeft={calcEntityIcon(item)}
                                    IconRight={calcDetectedIcon(item)}
                                    onRemove={
                                        !disabled && item.name?.toLowerCase() !== KRBTGT
                                            ? () => handleResourceRemoval(item)
                                            : undefined
                                    }
                                    disabled={shouldEntityBeDisabled(item)}
                                />
                            );
                        })}
                    </List>
                </div>
            ) : (
                <div className="flex flex-col text-center h-[230px] w-full items-center justify-center">
                    <div className="h-11 flex flex-col justify-between">
                        <div className="font-medium text-16 text-sf-gray-800">
                            {searchTerm ? 'No Results' : `No ${entityType} yet`}
                        </div>
                        <div className="text-13 text-sf-gray-500">
                            {searchTerm ? 'Try refining your search' : 'Add items by clicking the "Add" button'}
                        </div>
                    </div>
                </div>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        handleImportFromCsv(file);
                    }
                    e.target.value = '';
                }}
            />
        </div>
    );
};

export default EntitiesPickerTab;
